import { Suspense, lazy } from "react";
import GlobalStyles from "./styles/GlobalStyles";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { IntercomProvider } from "react-use-intercom";
import { ErrorBoundary } from "react-error-boundary";

import { CheckoutContextProvider } from "./features/payments/CheckoutContext";
import { AccountContextProvider } from "./features/account/AccountContext";
import { AuthContextProvider } from "./features/authentication/AuthContext";
import { SessionProvider } from "./context/SessionContext";
import { PlayerProvider } from "./context/PlayerContext";

// Import loading fallback components
import AuthLoadingFallback from "./features/authentication/AuthLoadingFallback";
import GeneralLoadingFallback from "./ui/GeneralLoadingFallback";
import AccountLoadingFallback from "./ui/AccountLoadingFallback";
import ContentOnlyLoadingFallback from "./ui/ContentOnlyLoadingFallback";
// WHY: Skeleton loaders for account pages — used as Suspense fallbacks during
// lazy chunk download so users see layout placeholders instead of blank spinners
import {
  ProfileOverviewSkeleton,
  ProfileEditSkeleton,
  BillingEditSkeleton,
  CommunicationPreferencesSkeleton,
  SubscriptionsSkeleton,
  PaymentMethodsSkeleton,
  PurchasesSkeleton,
  BillingOverviewSkeleton,
  AddPaymentMethodSkeleton,
} from "./features/account/AccountPageSkeleton";
import SpinnerFullPage from "./ui/SpinnerFullPage";
import ErrorFallback from "./ui/ErrorFallback";
import { QUERY_STALE_TIME_MS } from "./utils/constants.ts";

// Core components that should load immediately
import ProtectedRoute from "./ui/ProtectedRoute";
import MaintenanceProtector from "./ui/MaintenanceProtector";
import ChannelThemeLoader from "./ui/ChannelThemeLoader";

// Lazy load main pages for better performance
const Home = lazy(() => import("./pages/Home"));
const PageNotFound = lazy(() => import("./pages/PageNotFound"));
const AccountManagement = lazy(() => import("./pages/AccountManagement.tsx"));
const Legal = lazy(() => import("./pages/Legal"));

// Lazy load feature components
const ModalPlans = lazy(() => import("./features/plans/Plans"));
const StreamController = lazy(() => import("./features/player/StreamController"));
const PlaylistItems = lazy(() => import("./features/playlists/PlaylistItems"));
const AllUpcoming = lazy(() => import("./features/playlists/AllUpcoming"));
const CollectionItems = lazy(() => import("./features/collections/CollectionItems"));

// Lazy load legal components
const Terms = lazy(() => import("./features/legal/Terms"));
const Privacy = lazy(() => import("./features/legal/Privacy"));

// Lazy load authentication components
const Signup = lazy(() => import("./features/authentication/SignupForm"));
const ConfirmSignup = lazy(() => import("./features/authentication/ConfirmForm"));
const ResetPasswordForm = lazy(() => import("./features/authentication/ResetPasswordForm"));
const RequestPasswordResetForm = lazy(() => import("./features/authentication/RequestPasswordResetForm"));
const LoginForm = lazy(() => import("./features/authentication/LoginForm"));
const ResetPasswordSuccess = lazy(() => import("./features/authentication/ResetPasswordSuccess"));
const RegisterMFA = lazy(() => import("./features/authentication/RegisterMFA"));
const RequestPhoneVerification = lazy(() => import("./features/authentication/RequestPhoneVerification"));
const Account = lazy(() => import("./features/authentication/Account"));

// Lazy load account management components (all converted to TypeScript)
const ChangeEmailForm = lazy(() => import("./features/account/ChangeEmailForm.tsx"));
const TransferAccount = lazy(() => import("./features/account/TransferAccount.tsx"));
const TransferAccountVerification = lazy(() => import("./features/account/TransferAccountVerification.tsx"));
const TransferAccountSuccess = lazy(() => import("./features/account/TransferAccountSuccess.tsx"));
const ConfirmAttributeChange = lazy(() => import("./features/account/ConfirmAttributeChange.tsx"));
const ProfileOverview = lazy(() => import("./features/account/ProfileOverview.tsx"));
const ProfileEdit = lazy(() => import("./features/account/ProfileEdit.tsx"));
const BillingEdit = lazy(() => import("./features/account/BillingEdit.tsx"));
const CommunicationPreferencesEdit = lazy(() => import("./features/account/CommunicationPreferencesEdit.tsx"));
const Subscriptions = lazy(() => import("./features/account/Subscriptions.tsx"));
const PaymentMethods = lazy(() => import("./features/account/PaymentMethods.tsx"));
const Purchases = lazy(() => import("./features/account/Purchases.tsx"));
const BillingOverview = lazy(() => import("./features/account/BillingOverview.tsx"));
const ChangePaymentMethodSuccess = lazy(() => import("./features/account/ChangePaymentMethodSuccess.tsx"));
const AddOrUpdatePaymentMethod = lazy(() => import("./features/account/AddOrUpdatePaymentMethod.tsx"));

// Lazy load payment components
const CheckoutForm = lazy(() => import("./features/payments/CheckoutFormNew"));
const CheckoutSuccess = lazy(() => import("./features/payments/CheckoutSuccess"));
const CheckoutFailed = lazy(() => import("./features/payments/CheckoutFailed"));

// Lazy load dev components (DEV only - guarded so production builds exclude entirely)
const EmailPreviewShell = import.meta.env.DEV
  ? lazy(() => import("./features/email-preview/components/EmailPreviewShell"))
  : null;

// ReactQuery Config
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME_MS,
    },
  },
});

function App() {
  const theme = createTheme({
    typography: {
      fontSize: 22,
    },
  });

  const helmetContext = {};

  return (
    <HelmetProvider context={helmetContext}>
      <QueryClientProvider client={queryClient}>
        <ChannelThemeLoader>
          <MaintenanceProtector>
            <BrowserRouter>
              <Toaster />
              <AuthContextProvider>
                <SessionProvider>
                  <AccountContextProvider>
                    <ThemeProvider theme={theme}>
                      <CheckoutContextProvider>
                        <IntercomProvider>
                          <GlobalStyles />
                        <PlayerProvider>
                          <Routes>
                            <Route path="/" element={
                              <Suspense fallback={<SpinnerFullPage />}>
                                <Home />
                              </Suspense>
                            }>
                              <Route path="*" element={
                                <Suspense fallback={<GeneralLoadingFallback />}>
                                  <PageNotFound />
                                </Suspense>
                              } />
                              <Route
                                path="/media/:mediaId"
                                element={
                                  <Suspense fallback={<GeneralLoadingFallback />}>
                                    <StreamController />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="/playlist/:playlistId"
                                element={
                                  <Suspense fallback={<GeneralLoadingFallback />}>
                                    <PlaylistItems />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="/collection/:collectionId"
                                element={
                                  <Suspense fallback={<GeneralLoadingFallback />}>
                                    <CollectionItems />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="/upcoming-live"
                                element={
                                  <Suspense fallback={<GeneralLoadingFallback />}>
                                    <AllUpcoming />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="/plans"
                                element={
                                  <Suspense fallback={<GeneralLoadingFallback />}>
                                    <ModalPlans showModal={true} />
                                  </Suspense>
                                }
                              />
                              {/* Wrap authentication routes in Suspense to prevent flashing */}
                              <Route path="/signup" element={
                                <Suspense fallback={<AuthLoadingFallback />}>
                                  <Signup />
                                </Suspense>
                              } />
                              <Route
                                path="/signup/confirm"
                                element={
                                  <Suspense fallback={<AuthLoadingFallback />}>
                                    <ConfirmSignup />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="/signup/alternative-verification"
                                element={
                                  <Suspense fallback={<AuthLoadingFallback />}>
                                    <RequestPhoneVerification />
                                  </Suspense>
                                }
                              />
                              <Route path="/signin" element={
                                <Suspense fallback={<AuthLoadingFallback />}>
                                  <LoginForm />
                                </Suspense>
                              } />
                              <Route
                                path="/signin/request-password"
                                element={
                                  <Suspense fallback={<AuthLoadingFallback />}>
                                    <RequestPasswordResetForm />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="/signin/reset-password"
                                element={
                                  <Suspense fallback={<AuthLoadingFallback />}>
                                    <ResetPasswordForm />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="/signin/reset-password/success"
                                element={
                                  <Suspense fallback={<AuthLoadingFallback />}>
                                    <ResetPasswordSuccess />
                                  </Suspense>
                                }
                              />
                              {/* WHY: Backend Cognito email templates link to /signin/confirm-change
                                  with ?code= param for email transfer verification.
                                  NOT wrapped in ProtectedRoute — the verification code itself
                                  is the authentication. Same pattern as /signup/confirm.
                                  If ProtectedRoute wrapped this, expired sessions would redirect
                                  to signin and mangle the base64-encoded params in the process. */}
                              <Route
                                path="/signin/confirm-change"
                                element={
                                  <Suspense fallback={<ContentOnlyLoadingFallback />}>
                                    <TransferAccountVerification />
                                  </Suspense>
                                }
                              />
                              <Route path="/legal" element={
                                <Suspense fallback={<GeneralLoadingFallback />}>
                                  <Legal />
                                </Suspense>
                              }>
                                <Route
                                  path="terms-of-use"
                                  element={
                                    <Suspense fallback={<GeneralLoadingFallback />}>
                                      <Terms />
                                    </Suspense>
                                  }
                                />
                                <Route path="privacy" element={
                                  <Suspense fallback={<GeneralLoadingFallback />}>
                                    <Privacy />
                                  </Suspense>
                                } />
                              </Route>

                              <Route
                                path="/account"
                                element={
                                  <ProtectedRoute>
                                    <ErrorBoundary
                                      FallbackComponent={ErrorFallback}
                                      onError={(error, errorInfo) => {
                                        // WHY: Log account errors for monitoring and debugging
                                        // SECURITY: In production, only log error message to avoid leaking account data
                                        if (import.meta.env.DEV) {
                                          console.error("Account management error:", error, errorInfo);
                                        } else {
                                          console.error("Account management error:", error.message || 'Unknown error');
                                        }
                                        // TODO: Send to error tracking service (Sentry, DataDog, etc.)
                                      }}
                                      onReset={() => {
                                        // WHY: Reset any account-specific state on error recovery
                                        window.location.href = "/account/profile";
                                      }}
                                    >
                                      <Suspense fallback={<AccountLoadingFallback />}>
                                        <AccountManagement />
                                      </Suspense>
                                    </ErrorBoundary>
                                  </ProtectedRoute>
                                }
                              >
                                <Route
                                  path="details"
                                  element={<Navigate replace to="/account/profile" />}
                                />
                                <Route path="profile" element={
                                  <Suspense fallback={<ProfileOverviewSkeleton />}>
                                    <ProfileOverview />
                                  </Suspense>
                                } />
                                <Route path="profile/edit" element={
                                  <Suspense fallback={<ProfileEditSkeleton />}>
                                    <ProfileEdit />
                                  </Suspense>
                                } />
                                <Route path="profile/billing/edit" element={
                                  <Suspense fallback={<BillingEditSkeleton />}>
                                    <BillingEdit />
                                  </Suspense>
                                } />
                                <Route path="profile/communication/edit" element={
                                  <Suspense fallback={<CommunicationPreferencesSkeleton />}>
                                    <CommunicationPreferencesEdit />
                                  </Suspense>
                                } />
                                <Route path="subscriptions" element={
                                  <Suspense fallback={<SubscriptionsSkeleton />}>
                                    <Subscriptions />
                                  </Suspense>
                                } />
                                <Route path="payment-methods" element={
                                  <Suspense fallback={<PaymentMethodsSkeleton />}>
                                    <PaymentMethods />
                                  </Suspense>
                                } />
                                <Route path="purchases" element={
                                  <Suspense fallback={<PurchasesSkeleton />}>
                                    <Purchases />
                                  </Suspense>
                                } />
                                <Route
                                  path="enable-mfa"
                                  element={
                                    <Suspense fallback={<AuthLoadingFallback />}>
                                      <RegisterMFA />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="change-email-address"
                                  element={
                                    <Suspense fallback={<ContentOnlyLoadingFallback />}>
                                      <ChangeEmailForm />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="confirm-change"
                                  element={
                                    <Suspense fallback={<ContentOnlyLoadingFallback />}>
                                      <ConfirmAttributeChange />
                                    </Suspense>
                                  }
                                />
                                <Route path="transfer" element={
                                  <Suspense fallback={<ContentOnlyLoadingFallback />}>
                                    <TransferAccount />
                                  </Suspense>
                                } />
                                <Route
                                  path="transfer/verify"
                                  element={
                                    <Suspense fallback={<ContentOnlyLoadingFallback />}>
                                      <TransferAccountVerification />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="transfer/success"
                                  element={
                                    <Suspense fallback={<ContentOnlyLoadingFallback />}>
                                      <TransferAccountSuccess />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="configure-mobile-number"
                                  element={
                                    <Suspense fallback={<AuthLoadingFallback />}>
                                      <RequestPhoneVerification />
                                    </Suspense>
                                  }
                                />
                                <Route path="billing" element={
                                  <Suspense fallback={<BillingOverviewSkeleton />}>
                                    <BillingOverview />
                                  </Suspense>
                                } />
                                <Route
                                  path="add-payment-method"
                                  element={
                                    <Suspense fallback={<AddPaymentMethodSkeleton />}>
                                      <AddOrUpdatePaymentMethod />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="change-payment-method/success"
                                  element={
                                    <Suspense fallback={<ContentOnlyLoadingFallback />}>
                                      <ChangePaymentMethodSuccess />
                                    </Suspense>
                                  }
                                />
                              </Route>
                            </Route>

                            {/* Checkout routes - STANDALONE, not nested in account */}
                            {/* DEV-1922: ErrorBoundary catches intermittent render crashes
                                (race conditions, stale localStorage, network failures)
                                and shows a recoverable error UI instead of white screen */}
                            <Route
                              path="/checkout"
                              element={
                                <ProtectedRoute>
                                  <ErrorBoundary
                                    FallbackComponent={ErrorFallback}
                                    onError={(error, errorInfo) => {
                                      console.error("Checkout error:", import.meta.env.DEV ? error : error.message || 'Unknown error');
                                    }}
                                    onReset={() => {
                                      // Clear potentially corrupted cart state
                                      try {
                                        localStorage.removeItem('cart');
                                        localStorage.removeItem('checkout');
                                      } catch (e) { /* ignore */ }
                                      window.location.href = "/";
                                    }}
                                  >
                                    <Suspense fallback={<SpinnerFullPage />}>
                                      <CheckoutForm />
                                    </Suspense>
                                  </ErrorBoundary>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/checkout/success"
                              element={
                                <ProtectedRoute>
                                  <Suspense fallback={<GeneralLoadingFallback />}>
                                    <CheckoutSuccess />
                                  </Suspense>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/checkout/error"
                              element={
                                <ProtectedRoute>
                                  <Suspense fallback={<GeneralLoadingFallback />}>
                                    <CheckoutFailed />
                                  </Suspense>
                                </ProtectedRoute>
                              }
                            />

                            {/* DEV-only email preview route */}
                            {import.meta.env.DEV && (
                              <Route
                                path="/dev/email-preview"
                                element={
                                  <Suspense fallback={<SpinnerFullPage />}>
                                    <EmailPreviewShell />
                                  </Suspense>
                                }
                              />
                            )}
                        </Routes>
                      </PlayerProvider>
                    </IntercomProvider>
                  </CheckoutContextProvider>
                </ThemeProvider>
                  </AccountContextProvider>
                </SessionProvider>
              </AuthContextProvider>
            </BrowserRouter>
          </MaintenanceProtector>
        </ChannelThemeLoader>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
